import { observer } from 'mobx-react-lite';

import OpenInNew from '@/assets/icons/icon-open-in-new.svg?react';
import { EMPTY_VALUE_DASH } from '@/constants/common';
import { useStore } from '@/stores/StoreProvider';

import styles from './BasicInfo.module.scss';
import PerformanceMetricScale from './PerformanceMetricScale/PerformanceMetricScale.tsx';
import QualityMetricScale from './QualityMetricScale/QualityMetricScale.tsx';
import SpreadV2Label from './SpreadV2Label/SpreadV2Label.tsx';

function BasicInfo() {
  const { diamond } = useStore('diamondsStore');

  if (diamond?.isCut) {
    return (
      <div className={styles.root}>
        <div className="container">
          {diamond?.hasAnyCutwiseMetricValue && <h2 className={styles.cutwiseGradesTitle}>Cutwise Grades</h2>}

          {diamond?.performance?.integral && !diamond?.performance?.integralV2 && (
            <PerformanceMetricScale
              metric={{
                rg: diamond.performance.integral.rg,
                d: diamond.performance.integral.d,
                sh: diamond?.cut?.shapeId,
              }}
              value={diamond.performance.integral.val}
              metricKey="integral"
              title="Shape Normalized Performance"
            />
          )}

          {diamond?.performance?.integralV2 && (
            <PerformanceMetricScale
              metric={{
                rg: diamond.performance.integralV2.rg,
                d: diamond.performance.integralV2.d,
                sh: diamond?.cut?.shapeId,
              }}
              value={diamond.performance.integralV2.val}
              metricKey="integralV2"
              title="Shape Normalized Performance v2 (Beta)"
            />
          )}

          {diamond?.performance?.fire && (
            <PerformanceMetricScale
              metric={{
                rg: diamond.performance.fire.rg,
                d: diamond.performance.fire.d,
              }}
              value={diamond.performance.fire.val}
              metricKey="fire"
              title="Fire"
            />
          )}

          {diamond?.performance?.brilliance && (
            <PerformanceMetricScale
              metric={{
                rg: diamond.performance.brilliance.rg,
                d: diamond.performance.brilliance.d,
              }}
              value={diamond.performance.brilliance.val}
              metricKey="brilliance"
              title="Brilliance"
            />
          )}

          {diamond?.performance?.symmetry && (
            <PerformanceMetricScale
              metric={{
                rg: diamond.performance.symmetry.rg,
                d: diamond.performance.symmetry.d,
              }}
              value={diamond.performance.symmetry.val}
              metricKey="symmetry"
              title="Optical Symmetry"
            />
          )}

          <PerformanceMetricScale
            metric={{
              ct: diamond?.spreadCt,
              pc: diamond?.spreadPc,
              ratio: 11,
              d: 0.1,
            }}
            rows={[
              { title: <SpreadV2Label />, value: (diamond?.spreadCt ?? 0) + (diamond?.carat ?? 0), unit: 'ct' },
              { title: 'Spread V2', value: diamond?.spreadCt, unit: 'ct' },
            ]}
            metricKey="spread"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container">
        <h2 className={styles.title}>LABORATORY / MANUFACTURER GRADE</h2>

        {diamond?.cut && (
          <div className={styles.raw}>
            <div>Cut shape</div>

            <div>{diamond.cut.title ?? EMPTY_VALUE_DASH}</div>
          </div>
        )}

        <div className={styles.raw}>
          <div>Carat weight</div>

          <div>{diamond?.carat ? `${diamond.carat} CT` : EMPTY_VALUE_DASH}</div>
        </div>

        <div className={styles.raw}>
          <div>Color</div>

          <div>{diamond?.color?.title ?? EMPTY_VALUE_DASH}</div>
        </div>

        {diamond?.color && (
          <div className={styles.raw}>
            <QualityMetricScale title={diamond.color.title} pathName="color" />
          </div>
        )}

        <div className={styles.raw}>
          <div>Clarity</div>

          <div>{diamond?.clarity?.title ?? EMPTY_VALUE_DASH}</div>
        </div>

        {diamond?.clarity && (
          <div className={styles.raw}>
            <QualityMetricScale title={diamond.clarity.title} pathName="clarity" />
          </div>
        )}

        {diamond?.polish?.title && (
          <div className={styles.raw}>
            <div>Polish</div>

            <div>{diamond?.polish.title}</div>
          </div>
        )}

        {diamond?.symmetry?.title && (
          <div className={styles.raw}>
            <div>Lab Symmetry</div>

            <div>{diamond?.symmetry.title}</div>
          </div>
        )}

        {diamond?.cutQuality?.title && (
          <div className={styles.raw}>
            <div>
              <span>{diamond.certificate?.laboratory} </span>

              <span>Cut grade</span>

              {diamond.certificate?.number && (
                <a target="_blank" className={styles.link} href={diamond.certificate.file ?? diamond.certificate.check}>
                  <span>&nbsp;(</span>

                  <span>{diamond.certificate.number}</span>

                  <OpenInNew width="20" height="20" />

                  <span>)</span>
                </a>
              )}
            </div>

            <div>{diamond?.cutQuality?.title}</div>
          </div>
        )}

        {diamond?.hasAnyCutwiseMetricValue && <h2 className={styles.cutwiseGradesTitle}>Cutwise Grades</h2>}

        {diamond?.performance?.integral && !diamond?.performance?.integralV2 && (
          <PerformanceMetricScale
            metric={{
              rg: diamond.performance.integral.rg,
              d: diamond.performance.integral.d,
              sh: diamond?.cut?.shapeId,
            }}
            value={diamond.performance.integral.val}
            metricKey="integral"
            title="Shape Normalized Performance"
          />
        )}

        {diamond?.performance?.integralV2 && (
          <PerformanceMetricScale
            metric={{
              rg: diamond.performance.integralV2.rg,
              d: diamond.performance.integralV2.d,
              sh: diamond?.cut?.shapeId,
            }}
            value={diamond.performance.integralV2.val}
            metricKey="integralV2"
            title="Shape Normalized Performance v2 (Beta)"
          />
        )}

        {diamond?.performance?.fire && (
          <PerformanceMetricScale
            metric={{
              rg: diamond.performance.fire.rg,
              d: diamond.performance.fire.d,
            }}
            value={diamond.performance.fire.val}
            metricKey="fire"
            title="Fire"
          />
        )}

        {diamond?.performance?.brilliance && (
          <PerformanceMetricScale
            metric={{
              rg: diamond.performance.brilliance.rg,
              d: diamond.performance.brilliance.d,
            }}
            value={diamond.performance.brilliance.val}
            metricKey="brilliance"
            title="Brilliance"
          />
        )}

        {diamond?.performance?.symmetry && (
          <PerformanceMetricScale
            metric={{
              rg: diamond.performance.symmetry.rg,
              d: diamond.performance.symmetry.d,
            }}
            value={diamond.performance.symmetry.val}
            metricKey="symmetry"
            title="Optical Symmetry"
          />
        )}

        <PerformanceMetricScale
          metric={{
            ct: diamond?.spreadCt,
            pc: diamond?.spreadPc,
            ratio: 11,
            d: 0.1,
          }}
          rows={[
            { title: <SpreadV2Label />, value: (diamond?.spreadCt ?? 0) + (diamond?.carat ?? 0), unit: 'ct' },
            { title: 'Spread V2', value: diamond?.spreadCt, unit: 'ct' },
          ]}
          metricKey="spread"
        />
      </div>
    </div>
  );
}

export default observer(BasicInfo);
